import { useEffect, useState } from "react";
import { get } from "@/utils/service";
import type { KanbanColumn } from "./Kanban";

export interface BoardStatus { id: string; title: string; dot: string; droppable?: boolean }

/**
 * Loads a Kanban board from a `<module>-board` endpoint: one request returns every status with
 * its real total and first rows. `statuses` picks and orders the columns shown. Reloads whenever
 * `refreshKey` or `params` change.
 */
export function useStatusBoard<T>(url: string, statuses: BoardStatus[], params: Record<string, string | undefined>, refreshKey: number, enabled = true) {
  const [columns, setColumns] = useState<KanbanColumn<T>[]>([]);
  const [loading, setLoading] = useState(true);
  const key = JSON.stringify(params);

  useEffect(() => {
    if (!enabled) return;
    let live = true;
    setLoading(true);
    get<{ data: { columns: Array<{ status: string; count: number; items: T[] }> } }>(url, { ...JSON.parse(key), per_column: 50 })
      .then((r) => {
        if (!live) return;
        const byStatus = new Map(r.data.columns.map((c) => [c.status, c]));
        setColumns(statuses.map((st) => ({ ...st, items: byStatus.get(st.id)?.items ?? [], count: byStatus.get(st.id)?.count ?? 0 })));
      })
      .catch(() => { if (live) setColumns(statuses.map((st) => ({ ...st, items: [], count: 0 }))); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // statuses are constant per page
  }, [url, key, refreshKey, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return { columns, loading };
}
